import { motion } from "framer-motion";
import {
  MapPin,
  Home,
  Waves,
  Train,
  Sun,
  ChevronDown,
  Check,
  Phone,
  Mail,
  ArrowRight,
  Star,
  TreePine,
  Building2,
  Ruler,
} from "lucide-react";
import { useRef } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const highlights = [
  { icon: Waves, text: "Ca. 200 m zum Wallersee" },
  { icon: Ruler, text: "Ca. 89 m² Wohnfläche" },
  { icon: Building2, text: "Maisonette über 2 Etagen" },
  { icon: Train, text: "Bahnanbindung nach Salzburg" },
  { icon: Sun, text: "Helle, südlich ausgerichtete Räume" },
  { icon: TreePine, text: "Dachterrasse & Außenbereich" },
  { icon: Home, text: "Moderner Grundriss, durchdacht" },
  { icon: Star, text: "Hochwertiger Ausbaustandard" },
];

const features = [
  { label: "Objektart", value: "Maisonettewohnung" },
  { label: "Wohnfläche", value: "ca. 89 m²" },
  { label: "Lage", value: "Seekirchen am Wallersee" },
  { label: "Entfernung zum See", value: "ca. 200 m" },
  { label: "Salzburg", value: "ca. 15 km" },
  { label: "Gebäude", value: "Mehrparteienhaus" },
  { label: "Zustand", value: "Modern & gepflegt" },
];

const rooms = [
  {
    title: "Großzügiger Wohnbereich",
    description:
      "Der lichtdurchflutete Wohn- und Essbereich bildet das Herzstück der Wohnung. Großformatige Fenster, hochwertige Bodenbeläge und eine durchdachte Raumaufteilung schaffen ein Ambiente, das Weite und Wärme gleichermaßen vermittelt.",
    img: "/images/wohnbereich1.jpeg",
  },
  {
    title: "Einbauküche & Essbereich",
    description:
      "Die moderne Einbauküche verbindet funktionale Präzision mit stilvollem Design. Hochwertige Geräte, großzügige Arbeitsflächen und eine offene Verbindung zum Essbereich machen diesen Raum zum Mittelpunkt des Familienlebens.",
    img: "/images/kueche1.jpeg",
  },
  {
    title: "Schlafzimmer",
    description:
      "Das ruhig gelegene Schlafzimmer überzeugt durch ein harmonisches Raumklima und ausreichend Platz für individuelle Einrichtungswünsche. Klare Linien und eine beruhigende Farbgestaltung laden zur Erholung ein.",
    img: "/images/schlafzimmer.jpg",
  },
  {
    title: "Badezimmer",
    description:
      "Das zeitlos gestaltete Badezimmer mit hochwertigen Armaturen und durchdachter Raumnutzung bietet alles, was modernes Wohnen verlangt. Helle Flächen, edle Materialien und kluge Details machen das Bad zum privaten Wohlfühlort.",
    img: "/images/bad1.jpeg",
  },
  {
    title: "Kinderzimmer",
    description:
      "Zwei helle Kinderzimmer bieten Raum für Kreativität, Spiel und Rückzug. Die durchdachte Raumstruktur lässt sich flexibel gestalten – heute Kinderzimmer, morgen Jugendzimmer oder Gästezimmer.",
    img: "/images/kinderzimmer1.jpg",
  },
  {
    title: "Home Office",
    description:
      "Ein dedizierter Arbeitsbereich, der ruhig und konzentriert arbeiten lässt – perfekt für hybrides Arbeiten oder kreative Projekte. Die Ausstattung verbindet Funktionalität mit einem angenehmen Arbeitsklima.",
    img: "/images/homeoffice.jpg",
  },
];

export default function HomePage() {
  const objectRef = useRef<HTMLElement>(null);

  const scrollToObject = () => {
    objectRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── NAVIGATION ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 border-b border-border/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
              <Home size={16} className="text-primary-foreground" />
            </div>
            <span
              className="text-sm font-medium text-muted-foreground tracking-widest uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Immobilien Exposé
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            {["lage", "objekt", "highlights", "kontakt"].map((id) => (
              <button
                key={id}
                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                className="text-xs tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors"
              >
                {id === "lage"
                  ? "Lage"
                  : id === "objekt"
                    ? "Objekt"
                    : id === "highlights"
                      ? "Highlights"
                      : "Kontakt"}
              </button>
            ))}
          </nav>
          <button
            onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
            className="hidden md:flex items-center gap-2 bg-primary text-primary-foreground text-xs tracking-widest uppercase px-5 py-2.5 rounded hover:bg-primary/90 transition-colors"
          >
            Anfrage stellen
          </button>
        </div>
      </header>

      {/* ── HERO ── */}
      <section id="hero" className="relative h-screen min-h-[600px] flex items-end pb-24">
        {/* Background image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="/images/wohnbereich2.jpeg"
            alt="Moderner Wohnbereich der Maisonettewohnung"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/40 to-primary/10" />
        </div>

        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-6 w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
              <div
                className="h-px w-12 bg-ring"
                style={{ backgroundColor: "oklch(0.65 0.12 50)" }}
              />
              <span className="text-xs tracking-[0.25em] uppercase text-primary-foreground/80">
                Exklusives Verkaufsexposé · Seekirchen am Wallersee
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-5xl md:text-6xl lg:text-7xl text-primary-foreground mb-6 leading-[1.05]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              Leben wo der
              <br />
              <span style={{ color: "oklch(0.85 0.08 50)" }}>Wallersee</span>
              <br />
              beginnt.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-base md:text-lg text-primary-foreground/80 max-w-xl mb-10 leading-relaxed"
            >
              Moderne Maisonettewohnung mit ca. 89 m² – ca. 200 Meter zum See, perfekte
              Verkehrsanbindung, hoher Freizeitwert. Seekirchen am Wallersee, Salzburg.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4">
              <button
                onClick={scrollToObject}
                className="flex items-center gap-2 px-7 py-3.5 rounded text-sm font-medium tracking-wide transition-all duration-200 hover:gap-3"
                style={{
                  backgroundColor: "oklch(0.65 0.12 50)",
                  color: "white",
                }}
              >
                Objekt entdecken <ArrowRight size={16} />
              </button>
              <button
                onClick={() => document.getElementById("lage")?.scrollIntoView({ behavior: "smooth" })}
                className="flex items-center gap-2 px-7 py-3.5 rounded text-sm font-medium tracking-wide border border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 transition-all duration-200"
              >
                Zur Lage
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <button
          onClick={() => document.getElementById("eckdaten")?.scrollIntoView({ behavior: "smooth" })}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-primary-foreground/60 hover:text-primary-foreground transition-colors"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          >
            <ChevronDown size={28} />
          </motion.div>
        </button>
      </section>

      {/* ── ECKDATEN BANNER ── */}
      <section id="eckdaten" className="bg-primary text-primary-foreground py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 divide-y md:divide-y-0 md:divide-x divide-primary-foreground/20">
            {[
              { label: "Wohnfläche", value: "ca. 89 m²" },
              { label: "Etagen", value: "Maisonette" },
              { label: "Zum See", value: "ca. 200 m" },
              { label: "Nach Salzburg", value: "ca. 15 km" },
            ].map((item) => (
              <div key={item.label} className="pt-4 md:pt-0 md:px-8 first:pl-0">
                <p
                  className="text-2xl md:text-3xl font-semibold mb-1"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  {item.value}
                </p>
                <p className="text-xs tracking-widest uppercase text-primary-foreground/60">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LAGE & UMGEBUNG ── */}
      <section id="lage" className="py-24 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            {/* Section header */}
            <motion.div variants={fadeUp} className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-12" style={{ backgroundColor: "oklch(0.65 0.12 50)" }} />
                <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">
                  Lage & Umgebung
                </span>
              </div>
              <h2
                className="text-4xl md:text-5xl text-foreground mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
              >
                Seekirchen am Wallersee –<br />
                <span className="text-muted-foreground">wo Natur auf Lebensqualität trifft.</span>
              </h2>
              <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
                Seekirchen am Wallersee ist eine Stadt im Salzburger Flachgau mit rund 11.570 Einwohnern
                und einer beneidenswerten Lage direkt am Ufer des Wallersees. Wer hier wohnt, lebt
                nicht im Kompromiss zwischen Stadt und Natur – er hat beides.
              </p>
            </motion.div>

            {/* Seekirchen photo grid */}
            <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
              <div className="md:col-span-2 relative overflow-hidden rounded-lg aspect-[16/10]">
                <img
                  src="/images/seekirchen3.jpg"
                  alt="Luftaufnahme von Seekirchen am Wallersee"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                  <MapPin size={14} />
                  <span className="text-xs tracking-wide">Seekirchen am Wallersee, Salzburg</span>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <div className="relative overflow-hidden rounded-lg flex-1">
                  <img
                    src="/images/seekirchen1.jpg"
                    alt="Historisches Schloss in Seekirchen"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="relative overflow-hidden rounded-lg flex-1">
                  <img
                    src="/images/seekirchen2.jpg"
                    alt="Wallersee bei Sonnenuntergang"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                    <Waves size={13} />
                    <span className="text-xs tracking-wide">Wallersee · Direktzugang</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Location features */}
            <motion.div variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: Waves,
                  title: "Direkt am See",
                  desc: "Zwei frei zugängliche Strandbäder, Schwimmen, Surfen und Bootfahren – Wassersportleben vor der Haustür.",
                },
                {
                  icon: Train,
                  title: "Bestens verbunden",
                  desc: "Direkte Bahnverbindung nach Salzburg (ca. 15 km). Ideal für Pendler und Städtereisen ohne Stress.",
                },
                {
                  icon: Sun,
                  title: "Hoher Freizeitwert",
                  desc: "Spazier- und Radwege, Erholungsgebiete, frische Luft – eine Umgebung, die Lebensqualität täglich spürbar macht.",
                },
                {
                  icon: MapPin,
                  title: "Gewachsene Ortsstruktur",
                  desc: "Traditionelle Gasthöfe, Cafés, ein vollständiges Nahversorgungsangebot und ein lebendiges Gemeinschaftsgefühl.",
                },
              ].map((item) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-300"
                >
                  <div
                    className="w-10 h-10 rounded flex items-center justify-center mb-4"
                    style={{ backgroundColor: "oklch(0.65 0.12 50 / 0.15)" }}
                  >
                    <item.icon size={20} style={{ color: "oklch(0.65 0.12 50)" }} />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2 text-sm tracking-wide">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── OBJEKTBESCHREIBUNG ── */}
      <section id="objekt" ref={objectRef} className="py-24 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-12" style={{ backgroundColor: "oklch(0.65 0.12 50)" }} />
                <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">
                  Das Objekt
                </span>
              </div>
              <h2
                className="text-4xl md:text-5xl text-foreground mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
              >
                Durchdacht. Modern.
                <br />
                <span className="text-muted-foreground">Ein Zuhause auf zwei Ebenen.</span>
              </h2>
              <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
                Die Maisonettewohnung verbindet die Großzügigkeit eines Einfamilienhauses mit den
                Vorzügen eines modernen Mehrparteienhauses. Auf zwei Etagen entfaltet sich ein
                Raumkonzept, das keine Wünsche offen lässt – funktional durchdacht und hochwertig
                ausgestattet.
              </p>
            </motion.div>

            {/* Eckdaten Tabelle */}
            <motion.div variants={fadeUp} className="bg-card border border-border rounded-lg p-8 mb-16">
              <h3
                className="text-xl text-foreground mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
              >
                Objektdaten im Überblick
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((f) => (
                  <div
                    key={f.label}
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <span className="text-xs tracking-widest uppercase text-muted-foreground">
                      {f.label}
                    </span>
                    <span
                      className="text-sm font-medium text-foreground"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {f.value}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Room Grid */}
            <motion.div variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {rooms.map((room) => (
                <motion.div
                  key={room.title}
                  variants={fadeUp}
                  className="group bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={room.img}
                      alt={room.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6">
                    <h3
                      className="text-lg text-foreground mb-3"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {room.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {room.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Dachterrasse */}
            <motion.div variants={fadeUp} className="mt-8">
              <div className="relative overflow-hidden rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                  <div className="relative overflow-hidden h-72 md:h-auto">
                    <img
                      src="/images/dachterrasse1.jpeg"
                      alt="Dachterrasse mit Außenmöbeln"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="relative overflow-hidden h-72 md:h-auto">
                    <img
                      src="/images/dachterrasse2.jpeg"
                      alt="Sonnige Dachterrasse"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
                <div className="bg-primary text-primary-foreground p-8">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-3 mb-3">
                      <Sun size={18} style={{ color: "oklch(0.85 0.08 50)" }} />
                      <span className="text-xs tracking-[0.25em] uppercase text-primary-foreground/70">
                        Besonderes Highlight
                      </span>
                    </div>
                    <h3
                      className="text-2xl md:text-3xl text-primary-foreground mb-3"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      Die Dachterrasse – Ihr privates Stück Himmel
                    </h3>
                    <p className="text-sm text-primary-foreground/80 leading-relaxed">
                      Das krönende Element dieser Maisonettewohnung: eine großzügige Dachterrasse, die
                      drinnen und draußen nahtlos verbindet. Ob Morgenkaffe im Sonnenaufgang, ein
                      entspannter Sommerabend mit Blick auf Seekirchen oder ein privates Dinner unter
                      freiem Himmel – diese Terrasse ist ein Lebensraum, der weit über das Gewöhnliche
                      hinausgeht.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Zweites Kinderzimmer – neu positioniert nach Dachterrasse */}
            <motion.div variants={fadeUp} className="mt-8">
              <div className="bg-card border border-border rounded-lg overflow-hidden group">
                <div className="relative overflow-hidden h-72">
                  <img
                    src="/images/kinderzimmer2.jpeg"
                    alt="Zweites Kinderzimmer"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-8">
                  <h3
                    className="text-xl text-foreground mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                  >
                    Zweites Kinderzimmer
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed max-w-2xl">
                    Helle, freundliche Atmosphäre mit großen Fenstern und einer einladenden Farbgestaltung –
                    ein Raum, der mit seinen Bewohnern mitwächst und immer neue Möglichkeiten bietet.
                    Heute Kinderzimmer, morgen Jugendzimmer oder Gästezimmer.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── HIGHLIGHTS ── */}
      <section id="highlights" className="py-24 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="mb-16 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="h-px w-12" style={{ backgroundColor: "oklch(0.65 0.12 50)" }} />
                <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">
                  Auf einen Blick
                </span>
                <div className="h-px w-12" style={{ backgroundColor: "oklch(0.65 0.12 50)" }} />
              </div>
              <h2
                className="text-4xl md:text-5xl text-foreground"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
              >
                Das spricht für dieses Objekt.
              </h2>
            </motion.div>

            <motion.div variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {highlights.map((item) => (
                <motion.div
                  key={item.text}
                  variants={fadeUp}
                  className="flex items-start gap-3 p-5 bg-card border border-border rounded-lg hover:border-ring/40 transition-colors duration-200"
                  style={{ "--tw-border-opacity": "1" } as React.CSSProperties}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: "oklch(0.65 0.12 50 / 0.15)" }}
                  >
                    <item.icon size={15} style={{ color: "oklch(0.65 0.12 50)" }} />
                  </div>
                  <span className="text-sm text-foreground leading-snug font-medium">
                    {item.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* Zielgruppe */}
            <motion.div
              variants={fadeUp}
              className="bg-card border border-border rounded-xl p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px w-12" style={{ backgroundColor: "oklch(0.65 0.12 50)" }} />
                    <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">
                      Für wen ist dieses Objekt?
                    </span>
                  </div>
                  <h3
                    className="text-2xl md:text-3xl text-foreground mb-5"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                  >
                    Eine Wohnung für Menschen mit Anspruch.
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Diese Maisonettewohnung spricht Menschen an, die in ihrer Wohnsituation keine
                    Kompromisse eingehen wollen: Familien, die Platz und Natur gleichermaßen suchen.
                    Paare, die das Beste beider Welten wollen – Ruhe und Verbindung. Pendler, die
                    Salzburg nah, aber den Alltag entspannt erleben möchten. Und Anleger, die eine
                    langfristig wertstabile, vermietbare Qualitätswohnung in einer Wachstumsregion
                    suchen.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    "Familien, die Platz und natürliche Umgebung schätzen",
                    "Paare, die urban leben und Natur genießen möchten",
                    "Pendler mit Anschluss nach Salzburg",
                    "Home-Office-Nutzer mit Platzbedarf",
                    "Kapitalanleger mit Fokus auf wertstabile Lagen",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: "oklch(0.65 0.12 50 / 0.2)" }}
                      >
                        <Check size={11} style={{ color: "oklch(0.65 0.12 50)" }} />
                      </div>
                      <span className="text-sm text-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FAZIT / CTA ── */}
      <section
        id="kontakt"
        className="relative py-24 overflow-hidden"
        style={{ backgroundColor: "oklch(0.28 0.03 240)" }}
      >
        <div className="absolute inset-0 opacity-15">
          <img
            src="/images/seekirchen2.jpg"
            alt="Wallersee Hintergrundbild"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="text-center"
          >
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-12" style={{ backgroundColor: "oklch(0.65 0.12 50)" }} />
              <span className="text-xs tracking-[0.25em] uppercase text-primary-foreground/70">
                Fazit
              </span>
              <div className="h-px w-12" style={{ backgroundColor: "oklch(0.65 0.12 50)" }} />
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-6xl text-primary-foreground mb-8 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              Eine seltene Gelegenheit –<br />
              <span style={{ color: "oklch(0.85 0.08 50)" }}>
                am See, in der Stadt, in Reichweite.
              </span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="text-base text-primary-foreground/80 max-w-2xl mx-auto mb-12 leading-relaxed"
            >
              Diese Maisonettewohnung in Seekirchen am Wallersee ist mehr als eine Wohnung – sie ist
              eine Entscheidung für Lebensqualität. Die Kombination aus moderner Architektur, direkter
              Seenähe, ausgezeichneter Infrastruktur und der Nähe zu Salzburg macht dieses Objekt zu
              einer der überzeugendsten Immobilienmöglichkeiten in der Region. Sehen Sie selbst – wir
              freuen uns auf Ihre Anfrage.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <a
                href="tel:+437203016001000"
                className="flex items-center gap-2 px-8 py-4 rounded text-sm font-medium tracking-wide transition-all duration-200 hover:opacity-90"
                style={{
                  backgroundColor: "oklch(0.65 0.12 50)",
                  color: "white",
                }}
              >
                <Phone size={16} />
                Telefonisch anfragen
              </a>
              <a
                href="mailto:office@loft-projekt.com"
                className="flex items-center gap-2 px-8 py-4 rounded text-sm font-medium tracking-wide border border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 transition-all duration-200"
              >
                <Mail size={16} />
                Per E-Mail kontaktieren
              </a>
            </motion.div>

            {/* Quick facts footer */}
            <motion.div
              variants={fadeUp}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-12 border-t border-primary-foreground/20"
            >
              {[
                { label: "Objektart", value: "Maisonettewohnung" },
                { label: "Lage", value: "Seekirchen am Wallersee" },
                { label: "Wohnfläche", value: "ca. 89 m²" },
                { label: "Zustand", value: "Modern & gepflegt" },
              ].map((item) => (
                <div key={item.label}>
                  <p className="text-xs tracking-widest uppercase text-primary-foreground/50 mb-1">
                    {item.label}
                  </p>
                  <p
                    className="text-sm text-primary-foreground font-medium"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-foreground text-background py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-background/20 flex items-center justify-center">
              <Home size={14} className="text-background" />
            </div>
            <span className="text-xs tracking-widest uppercase text-background/60">
              Immobilien Exposé · Seekirchen am Wallersee
            </span>
          </div>
          <p className="text-xs text-background/40">
            Alle Angaben ohne Gewähr. Irrtümer und Änderungen vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
}
